import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';
import '../styles/ShoppingListGenerator.css';

const ShoppingListGenerator = ({ 
  isOpen, 
  onClose, 
  selectedRecipes = [], 
  onShoppingListCreated 
}) => {
  const [step, setStep] = useState(1); // 1: Preview, 2: Options, 3: Success
  const [previewData, setPreviewData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [existingLists, setExistingLists] = useState([]);
  
  // Form state
  const [shoppingListName, setShoppingListName] = useState('');
  const [addToExisting, setAddToExisting] = useState(false);
  const [selectedExistingList, setSelectedExistingList] = useState('');
  const [editableItems, setEditableItems] = useState([]);

  const loadPreview = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const recipeIds = selectedRecipes.map(recipe => recipe.id);
      const response = await apiService.previewShoppingList(recipeIds);
      setPreviewData(response);
      
      // Initialize editable items
      setEditableItems(response.neededItems.map(item => ({
        ...item,
        selected: true,
        customQuantity: item.quantity,
        customNotes: item.notes
      })));
      
      // Set default shopping list name
      if (response.recipes.length === 1) {
        setShoppingListName(`Shopping for ${response.recipes[0].title}`);
      } else {
        setShoppingListName(`Shopping for ${response.recipes.length} recipes`);
      }
      
    } catch (error) {
      console.error('Error loading preview:', error);
      setError('Failed to load shopping list preview. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedRecipes]);

  const loadExistingLists = async () => {
    try {
      const response = await apiService.getShoppingListsForGenerator();
      setExistingLists(response.lists.filter(list => list.status === 'active'));
    } catch (error) {
      console.error('Error loading existing lists:', error);
    }
  };

  // Initialize component
  useEffect(() => {
    if (isOpen && selectedRecipes.length > 0) {
      setStep(1);
      setError(null);
      loadPreview();
      loadExistingLists();
    }
  }, [isOpen, selectedRecipes, loadPreview]);

  const handleItemToggle = (index) => {
    setEditableItems(prev => prev.map((item, i) => 
      i === index ? { ...item, selected: !item.selected } : item
    ));
  };

  const handleQuantityChange = (index, quantity) => {
    setEditableItems(prev => prev.map((item, i) => 
      i === index ? { ...item, customQuantity: quantity } : item
    ));
  };

  const handleNotesChange = (index, notes) => {
    setEditableItems(prev => prev.map((item, i) => 
      i === index ? { ...item, customNotes: notes } : item
    ));
  };

  const handleGenerateShoppingList = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const selectedItems = editableItems.filter(item => item.selected);
      
      if (selectedItems.length === 0) {
        setError('Please select at least one item to add to your shopping list.');
        setIsLoading(false);
        return;
      }

      const recipeIds = selectedRecipes.map(recipe => recipe.id);
      const options = {
        shoppingListName: addToExisting ? undefined : shoppingListName,
        addToExistingList: addToExisting,
        existingListId: addToExisting ? selectedExistingList : undefined
      };

      let response;
      if (recipeIds.length === 1) {
        response = await apiService.generateShoppingListFromRecipe(recipeIds[0], options);
      } else {
        response = await apiService.generateShoppingListFromRecipes(recipeIds, options);
      }

      // Update the shopping list with custom quantities and notes
      if (response.shoppingList && selectedItems.length > 0) {
        // Note: In a real implementation, you'd want to update the items with custom data
        // For now, we'll use the generated items as-is
      }

      setStep(3);
      
      if (onShoppingListCreated) {
        onShoppingListCreated(response.shoppingList);
      }
      
    } catch (error) {
      console.error('Error generating shopping list:', error);
      setError('Failed to generate shopping list. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setPreviewData(null);
    setEditableItems([]);
    setError(null);
    setShoppingListName('');
    setAddToExisting(false);
    setSelectedExistingList('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="shopping-list-generator-overlay">
      <div className="shopping-list-generator-modal">
        <div className="generator-header">
          <h2>Generate Shopping List</h2>
          <button className="close-btn" onClick={handleClose}>x</button>
        </div>

        <div className="generator-content">
          {step === 1 && (
            <div className="preview-step">
              <div className="step-header">
                <h3>Preview Shopping List</h3>
                <p>Review the items you need to buy for your selected recipes.</p>
              </div>

              {isLoading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Analyzing your pantry and recipes...</p>
                </div>
              ) : error ? (
                <div className="error-state">
                  <span className="error-icon">Warning</span>
                  <p>{error}</p>
                  <button className="retry-btn" onClick={loadPreview}>
                    Try Again
                  </button>
                </div>
              ) : previewData ? (
                <div className="preview-content">
                  <div className="preview-summary">
                    <div className="summary-card">
                      <span className="summary-number">{previewData.recipes.length}</span>
                      <span className="summary-label">Recipe{previewData.recipes.length > 1 ? 's' : ''}</span>
                    </div>
                    <div className="summary-card">
                      <span className="summary-number">{previewData.totalIngredients}</span>
                      <span className="summary-label">Total Ingredients</span>
                    </div>
                    <div className="summary-card">
                      <span className="summary-number">{previewData.pantryItems}</span>
                      <span className="summary-label">Pantry Items</span>
                    </div>
                    <div className="summary-card highlight">
                      <span className="summary-number">{previewData.itemsNeeded}</span>
                      <span className="summary-label">Items Needed</span>
                    </div>
                  </div>

                  <div className="recipes-list">
                    <h4>Selected Recipes:</h4>
                    <ul>
                      {previewData.recipes.map(recipe => (
                        <li key={recipe.id}>{recipe.title}</li>
                      ))}
                    </ul>
                  </div>
                  {previewData.availableItems && previewData.availableItems.length > 0 && (
                    <div className="recipes-list">
                      <h4>Using From Pantry:</h4>
                      <ul>
                        {previewData.availableItems.map((item, index) => (
                          <li key={`${item.name}-${index}`}>
                            {item.name} ({item.pantryQuantity} in pantry)
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}


                  {previewData.itemsNeeded === 0 ? (
                    <div className="all-available">
                      <div className="success-icon">Success</div>
                      <h4>Great news!</h4>
                      <p>You already have all the ingredients needed for these recipes in your pantry.</p>
                      <button className="btn-primary" onClick={handleClose}>
                        Close
                      </button>
                    </div>
                  ) : (
                    <div className="items-preview">
                      <h4>Items to add to shopping list:</h4>
                      <div className="items-list">
                        {editableItems.map((item, index) => (
                          <div key={index} className={`item-preview ${!item.selected ? 'unselected' : ''}`}>
                            <div className="item-checkbox">
                              <input
                                type="checkbox"
                                checked={item.selected}
                                onChange={() => handleItemToggle(index)}
                              />
                            </div>
                            <div className="item-details">
                              <div className="item-name">{item.name}</div>
                              <div className="item-quantity">
                                <input
                                  type="text"
                                  value={item.customQuantity}
                                  onChange={(e) => handleQuantityChange(index, e.target.value)}
                                  className="quantity-input"
                                />
                              </div>
                              <div className="item-notes">
                                <input
                                  type="text"
                                  value={item.customNotes}
                                  onChange={(e) => handleNotesChange(index, e.target.value)}
                                  className="notes-input"
                                  placeholder="Add notes..."
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="preview-actions">
                        <button 
                          className="btn-secondary" 
                          onClick={handleClose}
                        >
                          Cancel
                        </button>
                        <button 
                          className="btn-primary" 
                          onClick={() => setStep(2)}
                          disabled={editableItems.filter(item => item.selected).length === 0}
                        >
                          Continue ({editableItems.filter(item => item.selected).length} items)
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}

          {step === 2 && (
            <div className="options-step">
              <div className="step-header">
                <h3>Shopping List Options</h3>
                <p>Choose how you want to create your shopping list.</p>
              </div>

              <div className="options-content">
                <div className="option-group">
                  <label className="option-label">
                    <input
                      type="radio"
                      name="listOption"
                      checked={!addToExisting}
                      onChange={() => setAddToExisting(false)}
                    />
                    <span>Create new shopping list</span>
                  </label>
                  
                  {!addToExisting && (
                    <div className="option-details">
                      <label htmlFor="listName">Shopping List Name:</label>
                      <input
                        id="listName"
                        type="text"
                        value={shoppingListName}
                        onChange={(e) => setShoppingListName(e.target.value)}
                        className="list-name-input"
                        placeholder="Enter shopping list name..."
                      />
                    </div>
                  )}
                </div>

                <div className="option-group">
                  <label className="option-label">
                    <input
                      type="radio"
                      name="listOption"
                      checked={addToExisting}
                      onChange={() => setAddToExisting(true)}
                    />
                    <span>Add to existing shopping list</span>
                  </label>
                  
                  {addToExisting && (
                    <div className="option-details">
                      <label htmlFor="existingList">Select List:</label>
                      <select
                        id="existingList"
                        value={selectedExistingList}
                        onChange={(e) => setSelectedExistingList(e.target.value)}
                        className="existing-list-select"
                      >
                        <option value="">Choose a shopping list...</option>
                        {existingLists.map(list => (
                          <option key={list.id} value={list.id}>
                            {list.name} ({list.itemCount} items)
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              <div className="options-actions">
                <button 
                  className="btn-secondary" 
                  onClick={() => setStep(1)}
                >
                  Back
                </button>
                <button 
                  className="btn-primary" 
                  onClick={handleGenerateShoppingList}
                  disabled={isLoading || (!addToExisting && !shoppingListName.trim()) || (addToExisting && !selectedExistingList)}
                >
                  {isLoading ? 'Creating...' : 'Generate Shopping List'}
                </button>
              </div>

              {error && (
                <div className="error-message">
                  <span className="error-icon">Warning</span>
                  {error}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="success-step">
              <div className="success-content">
                <div className="success-icon">Success</div>
                <h3>Shopping List Created!</h3>
                <p>Your shopping list has been successfully generated and saved.</p>
                
                <div className="success-actions">
                  <button 
                    className="btn-primary" 
                    onClick={() => {
                      handleClose();
                      // Navigate to shopping lists page
                      window.location.href = '/shopping';
                    }}
                  >
                    View Shopping Lists
                  </button>
                  <button 
                    className="btn-secondary" 
                    onClick={handleClose}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShoppingListGenerator;

